import { jsx, jsxs } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { useResponsiveValue, Box, Stack, ButtonIcon, IconChevron, Hidden } from "braid-design-system";
import { useState, createRef } from "react";
import { translations } from "../../../../../components/.vocab/index.mjs";
import { usePaginationAriaLabel, BulletPoints } from "../BulletPoints/BulletPoints.mjs";
import { trackCarouselNavigation } from "../../../BrandCard/BrandCardWithAssetLibrary.mjs";
import { CompanyProfileCard } from "../CompanyProfileCards/CompanyProfileCards.mjs";
import { arrows, carousel, slideContainer, carouselItem } from "./Carousel.css.mjs";
const getCarouselSlides = (cards, options = {}) => {
  const {
    itemPerSlide = MAX_ITEMS_PER_SLIDE,
    maxItems = ITEMS_PER_SLIDE_MAPPING[itemPerSlide]
  } = options;
  const slides = cards.slice(0, maxItems).reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / itemPerSlide);
    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }
    resultArray[chunkIndex].push(item);
    return resultArray;
  }, []);
  return slides;
};
const getCarouselCardPositionHelper = (itemPerSlide) => (slideIndex, cardIndex) => slideIndex * itemPerSlide + cardIndex + 1;
const MAX_ITEMS_PER_SLIDE = 5;
const MAX_ITEMS = 15;
const MAX_ITEMS_PER_SLIDE_FOR_NEW_HOMEPAGE_LAYOUT = 4;
const MAX_ITEMS_FOR_NEW_HOMEPAGE_LAYOUT = 16;
const ITEMS_PER_SLIDE_MAPPING = {
  [MAX_ITEMS_PER_SLIDE]: MAX_ITEMS,
  [MAX_ITEMS_PER_SLIDE_FOR_NEW_HOMEPAGE_LAYOUT]: MAX_ITEMS_FOR_NEW_HOMEPAGE_LAYOUT
};
const X_AXIS_RANGE_DELTA = 10;
const Carousel = ({
  companyProfiles,
  onReviewsLandingPage,
  showReviews,
  showJobs,
  analyticsQueryParams,
  payload,
  itemPerSlide,
  featureFlags,
  onCardImpression
}) => {
  const { t } = useTranslations(translations);
  const slides = getCarouselSlides(companyProfiles, { itemPerSlide });
  const [focusedSlideIndex, setFocusedSlideIndex] = useState(0);
  const isLastSlide = focusedSlideIndex >= slides.length - 1;
  const isFirstSlide = focusedSlideIndex <= 0;
  const ref = createRef();
  const responsiveValue = useResponsiveValue();
  const getCarouselCardPosition = getCarouselCardPositionHelper(itemPerSlide);
  const inLineStyleForItemInLastSlide = responsiveValue({
    mobile: {},
    desktop: {
      flexBasis: `${Math.round(1 / itemPerSlide * 100)}%`,
      flexGrow: 0,
      flexShrink: 1
    }
  }) || {};
  const moveSlidesLeft = (event, slidesToMove = 1) => {
    event.preventDefault();
    trackCarouselNavigation(payload, "Left");
    if (isFirstSlide) {
      return;
    }
    const carouselDiv = ref.current;
    if (carouselDiv) {
      carouselDiv.scrollLeft -= carouselDiv.clientWidth * slidesToMove;
    }
  };
  const moveSlidesRight = (event, slidesToMove = 1) => {
    event.preventDefault();
    trackCarouselNavigation(payload, "Right");
    if (isLastSlide) {
      return;
    }
    const carouselDiv = ref.current;
    if (carouselDiv) {
      carouselDiv.scrollLeft += carouselDiv.clientWidth * slidesToMove;
    }
  };
  const handleScroll = (event) => {
    var _a;
    event.preventDefault();
    const carouselDiv = ref.current;
    if (!carouselDiv) {
      return;
    }
    const carouselX = ((_a = carouselDiv.getBoundingClientRect()) == null ? void 0 : _a.x) || 0;
    const carouselXRangeMin = carouselX - X_AXIS_RANGE_DELTA;
    const carouselXRangeMax = carouselX + X_AXIS_RANGE_DELTA;
    Array.from(carouselDiv.children).some((item, index) => {
      const { x } = item.getBoundingClientRect();
      if (carouselXRangeMin <= x && x <= carouselXRangeMax) {
        setFocusedSlideIndex(index);
        return true;
      }
    });
  };
  const ariaLabel = usePaginationAriaLabel({
    pageNumber: focusedSlideIndex + 1,
    totalPages: slides.length
  });
  const isDesktopOrAbove = responsiveValue({
    mobile: false,
    desktop: true
  });
  return /* @__PURE__ */ jsx(
    Box,
    {
      role: "region",
      "aria-label": t("Slideshow: Employers with jobs available"),
      children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        /* @__PURE__ */ jsxs(Box, { position: "relative", display: "flex", alignItems: "center", children: [
          /* @__PURE__ */ jsx(Box, { role: "status", display: "none", children: ariaLabel }),
          !isFirstSlide && /* @__PURE__ */ jsx(
            Box,
            {
              className: arrows.left,
              background: "surface",
              boxShadow: "borderNeutralLight",
              onClick: moveSlidesLeft,
              children: /* @__PURE__ */ jsx(
                ButtonIcon,
                {
                  id: "cp-carousel-previous",
                  icon: /* @__PURE__ */ jsx(IconChevron, { direction: "left", tone: "secondary" }),
                  size: "large",
                  label: t("Previous"),
                  variant: "transparent",
                  bleed: false
                }
              )
            }
          ),
          /* @__PURE__ */ jsx(Box, { className: carousel, ref, onScroll: handleScroll, children: slides.map((slide, indexSlide) => /* @__PURE__ */ jsx(Box, { className: slideContainer, children: slide.map((company, indexCompanyProfile) => /* @__PURE__ */ jsx(
            Box,
            {
              position: "relative",
              className: carouselItem,
              style: indexSlide === slides.length - 1 ? inLineStyleForItemInLastSlide : {},
              height: "full",
              paddingY: "xxsmall",
              children: /* @__PURE__ */ jsx(
                CompanyProfileCard,
                {
                  company,
                  showReviews,
                  showJobs,
                  onCardImpression,
                  analyticsQueryParams: analyticsQueryParams ?? "",
                  carouselCardPosition: getCarouselCardPosition(
                    indexSlide,
                    indexCompanyProfile
                  ),
                  onReviewsLandingPage,
                  payload,
                  featureFlags,
                  tabIndex: indexSlide !== focusedSlideIndex && isDesktopOrAbove ? -1 : 0
                }
              )
            },
            indexCompanyProfile
          )) }, indexSlide)) }),
          !isLastSlide && /* @__PURE__ */ jsx(
            Box,
            {
              className: arrows.right,
              background: "surface",
              boxShadow: "borderNeutralLight",
              onClick: moveSlidesRight,
              children: /* @__PURE__ */ jsx(
                ButtonIcon,
                {
                  id: "cp-carousel-next",
                  icon: /* @__PURE__ */ jsx(IconChevron, { direction: "right", tone: "secondary" }),
                  size: "large",
                  label: t("Next"),
                  variant: "transparent",
                  bleed: false
                }
              )
            }
          )
        ] }),
        /* @__PURE__ */ jsx(Hidden, { below: "desktop", children: /* @__PURE__ */ jsx(
          BulletPoints,
          {
            focusedSlideIndex,
            moveSlidesLeft,
            moveSlidesRight,
            onReviewsLandingPage,
            numberOfBulletPoints: slides.length
          }
        ) })
      ] })
    }
  );
};
export {
  Carousel,
  MAX_ITEMS,
  MAX_ITEMS_FOR_NEW_HOMEPAGE_LAYOUT,
  MAX_ITEMS_PER_SLIDE,
  MAX_ITEMS_PER_SLIDE_FOR_NEW_HOMEPAGE_LAYOUT
};
